import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import * as userApi from '@/data/userApi'
import { useUsers, type UserRecord } from '@/data/useUsers'
import { errorMessage } from '@/lib/errors'
import { AddUserModal } from './AddUserModal'
import { EditUserModal } from './EditUserModal'
import { UserTable } from './UserTable'

type Banner = { type: 'success' | 'error'; text: string } | null

const ROLE_FILTERS: { key: 'admin' | 'supplier' | 'client' | null; label: string }[] = [
  { key: null, label: 'All' },
  { key: 'admin', label: 'Admins' },
  { key: 'supplier', label: 'Suppliers' },
  { key: 'client', label: 'Clients' },
]

export function UsersPage() {
  const { users, loading, error } = useUsers()
  const [searchParams, setSearchParams] = useSearchParams()
  const roleParam = searchParams.get('role')
  const activeRole =
    roleParam === 'admin' || roleParam === 'supplier' || roleParam === 'client' ? roleParam : null
  const visibleUsers = activeRole ? users.filter((user) => user.role === activeRole) : users
  const [addOpen, setAddOpen] = useState(false)
  const [editUser, setEditUser] = useState<UserRecord | null>(null)
  const [pendingDelete, setPendingDelete] = useState<UserRecord | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [banner, setBanner] = useState<Banner>(null)

  async function handleToggleSuspend(user: UserRecord) {
    try {
      await userApi.setUserSuspended(user.id, !user.suspended)
      setBanner({
        type: 'success',
        text: `${user.name} ${user.suspended ? 'reactivated' : 'suspended'}.`,
      })
    } catch (err) {
      setBanner({ type: 'error', text: errorMessage(err) })
    }
  }

  async function handleConfirmDelete() {
    if (!pendingDelete) {
      return
    }
    setDeleting(true)
    try {
      await userApi.deleteUser(pendingDelete.id)
      setBanner({ type: 'success', text: `${pendingDelete.name} deleted.` })
      setPendingDelete(null)
    } catch (err) {
      setBanner({ type: 'error', text: errorMessage(err) })
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-800">Users</h1>
        <Button onClick={() => setAddOpen(true)}>Add user</Button>
      </div>

      <div className="mb-4 flex flex-wrap gap-1.5">
        {ROLE_FILTERS.map((filter) => (
          <button
            key={filter.label}
            type="button"
            onClick={() =>
              setSearchParams(filter.key ? { role: filter.key } : {}, { replace: true })
            }
            className={`rounded-full px-3 py-1 text-sm font-medium transition ${
              activeRole === filter.key
                ? 'bg-primary text-white'
                : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {banner && (
        <div
          className={`mb-4 rounded-lg px-4 py-2 text-sm ${
            banner.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          {banner.text}
        </div>
      )}

      {loading && <p className="text-slate-500">Loading users…</p>}
      {error && (
        <p role="alert" className="text-danger">
          {error}
        </p>
      )}
      {!loading && !error && (
        <UserTable
          users={visibleUsers}
          onEdit={setEditUser}
          onToggleSuspend={handleToggleSuspend}
          onDelete={setPendingDelete}
        />
      )}

      <AddUserModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={({ name }) => {
          setAddOpen(false)
          setBanner({ type: 'success', text: `${name} created.` })
        }}
      />
      <EditUserModal
        key={editUser?.id ?? 'none'}
        user={editUser}
        onClose={() => setEditUser(null)}
        onSaved={() => {
          setEditUser(null)
          setBanner({ type: 'success', text: 'User updated.' })
        }}
      />
      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete user"
        message={pendingDelete ? `Delete ${pendingDelete.name}? This cannot be undone.` : ''}
        confirmLabel="Delete"
        busy={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  )
}
